import { useState, useEffect } from 'react';

// Generic CRUD hook
export const useCrud = (service, entityName) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all records
  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await service.getAll();
      setData(response.data.data || response.data);
    } catch (err) {
      setError(`Erro ao carregar ${entityName}: ${err.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Create a new record
  const create = async (newData) => {
    try {
      setError(null);
      const response = await service.create(newData);
      const createdItem = response.data.data || response.data;
      setData(prev => [...prev, createdItem]);
      return { success: true, data: createdItem };
    } catch (err) {
      const errorMessage = `Erro ao criar ${entityName}: ${err.response?.data?.message || err.message}`;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Update a record
  const update = async (id, updatedData) => {
    try {
      setError(null);
      const response = await service.update(id, updatedData);
      const updatedItem = response.data.data || response.data;
      setData(prev => prev.map(item => 
        item.id === id ? { ...item, ...updatedItem } : item
      ));
      return { success: true, data: updatedItem };
    } catch (err) {
      const errorMessage = `Erro ao atualizar ${entityName}: ${err.response?.data?.message || err.message}`;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  // Delete a record
  const remove = async (id) => {
    try {
      setError(null);
      await service.delete(id);
      setData(prev => prev.filter(item => item.id !== id));
      return { success: true };
    } catch (err) {
      const errorMessage = `Erro ao excluir ${entityName}: ${err.response?.data?.message || err.message}`;
      setError(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  return {
    data,
    loading,
    error,
    create,
    update,
    remove,
    refresh: fetchData,
    setError
  };
};

// Hook for form state management
export const useForm = (initialState = {}, validationRules = {}) => {
  const [values, setValues] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const setValue = (name, value) => {
    setValues(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const setFieldTouched = (name) => {
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
  };

  const validate = () => {
    const newErrors = {};

    Object.keys(validationRules).forEach(field => {
      const rules = validationRules[field];
      const value = values[field];

      if (rules.required && (!value || value.toString().trim() === '')) {
        newErrors[field] = `${rules.label || field} é obrigatório`;
        return;
      }

      if (value && rules.minLength && value.toString().length < rules.minLength) {
        newErrors[field] = `${rules.label || field} deve ter pelo menos ${rules.minLength} caracteres`;
        return;
      }

      if (value && rules.maxLength && value.toString().length > rules.maxLength) {
        newErrors[field] = `${rules.label || field} deve ter no máximo ${rules.maxLength} caracteres`;
        return;
      }

      if (value && rules.pattern && !rules.pattern.test(value)) {
        newErrors[field] = rules.patternMessage || `${rules.label || field} tem formato inválido`;
        return;
      }

      if (rules.custom && typeof rules.custom === 'function') {
        const customError = rules.custom(value, values);
        if (customError) {
          newErrors[field] = customError;
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const reset = (newInitialState = initialState) => {
    setValues(newInitialState);
    setErrors({});
    setTouched({});
  };

  const handleSubmit = (onSubmit) => (e) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allFields = Object.keys(validationRules);
    const touchedState = {};
    allFields.forEach(field => {
      touchedState[field] = true;
    });
    setTouched(touchedState);

    if (validate()) {
      onSubmit(values);
    }
  };

  return {
    values,
    errors,
    touched,
    setValue,
    setTouched: setFieldTouched,
    validate,
    reset,
    handleSubmit,
    isValid: Object.keys(errors).length === 0
  };
};

// Hook for modal state management
export const useModal = (initialState = false) => {
  const [isOpen, setIsOpen] = useState(initialState);
  const [data, setData] = useState(null);

  const open = (modalData = null) => {
    setData(modalData);
    setIsOpen(true);
  };

  const close = () => {
    setIsOpen(false);
    setData(null);
  };

  return {
    isOpen,
    data,
    open,
    close
  };
};